// src/context/auth.context/index.tsx
import axios from 'axios';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

import { getSession as getUserSession } from '@/lib/auth/client/getSession';
import { login as loginUser } from '@/lib/auth/client/login';
import { logout as logoutUser } from '@/lib/auth/client/logout';
import { configurePosthogTracking } from '@/lib/posthog';
import rollbarClient from '@/lib/rollbar/rollbarClient';

import type { AuthContextProps, AuthData, Group, LoginCredentials, SessionData } from './auth.types';

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function AuthProvider({
  children,
  initialAuth = null,
}: {
  children: React.ReactNode;
  initialAuth?: AuthData | null;
}) {
  const router = useRouter();
  const posthog = usePostHog();
  const [auth, setAuth] = useState<AuthData | null>(initialAuth);
  const [loading, setLoading] = useState<boolean>(!initialAuth);

  const changeSelectedGroup = useCallback(
    (newGroup: Group) => {
      // set new group on auth state
      setAuth((prev) => (prev ? { ...prev, selectedGroup: newGroup, company: newGroup } : prev));
      // update posthog settings based on new group
      posthog.group('profile', String(newGroup.id), newGroup);
      posthog.capture('profile', { group: newGroup.id, profile: newGroup.name });
      // set new group on cookies/LS
      Cookies.set('group', String(newGroup.id));
      Cookies.set('groupName', newGroup.name);
    },
    [posthog]
  );

  const parseAuth = useCallback((data: SessionData): AuthData => {
    const groups = data.groups ?? [];
    const cookieGroupId = Cookies.get('group') ?? '';
    const resolvedGroup = groups.find((g) => String(g.id) === cookieGroupId) ?? groups[0];

    if (resolvedGroup) {
      Cookies.set('group', String(resolvedGroup.id));
      Cookies.set('groupName', resolvedGroup.name);
    }

    return {
      ...data,
      company: resolvedGroup,
      selectedGroup: resolvedGroup,
    };
  }, []);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setLoading(true);
      const res = await loginUser({ credentials, rollbar: rollbarClient });
      if (res?.logged) {
        const session = await getUserSession({ rollbar: rollbarClient });
        if (session?.user?.signed) {
          const parsedAuth = parseAuth(session);
          setAuth(parsedAuth);
          configurePosthogTracking({ auth: parsedAuth, posthog });
        }
      }

      setLoading(false);
      return res;
    },
    [parseAuth, posthog]
  );

  const logout = useCallback(async () => {
    const res = await logoutUser(rollbarClient);
    if (!res?.logged) {
      posthog.reset(true);
      setAuth(null);
      router.replace('/login');
    }
  }, [posthog, router]);

  useEffect(() => {
    if (initialAuth) {
      setLoading(false);
      return;
    }

    const initSession = async () => {
      try {
        const session = await getUserSession({ rollbar: rollbarClient });

        if (session?.user?.email) {
          const parsedAuth = parseAuth(session);
          setAuth(parsedAuth);
          configurePosthogTracking({ auth: parsedAuth, posthog });
        } else {
          setAuth(null);
        }
      } catch (err: unknown) {
        if (axios.isAxiosError(err)) {
          const status = err.response?.status;
          if (status === 401 || status === 403) {
            setAuth(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    initSession();
  }, [initialAuth, parseAuth, posthog]);

  const value = useMemo(
    () => ({
      auth,
      loading,
      login,
      logout,
      changeSelectedGroup,
    }),
    [auth, loading, login, logout, changeSelectedGroup]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextProps {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
